/**
 * WeaveIn.ai Platform - Invoice Management API Service
 * TypeScript service layer for invoice CRUD, email sending, PDF generation,
 * payment tracking, and portal link management.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type InvoiceStatus =
  | 'draft'
  | 'open'
  | 'sent'
  | 'viewed'
  | 'overdue'
  | 'paid'
  | 'void'
  | 'uncollectible';

export type LineItemType =
  | 'product'
  | 'service'
  | 'subscription'
  | 'usage'
  | 'one_time'
  | 'credit'
  | 'discount'
  | 'tax';

export type PaymentMethod =
  | 'credit_card'
  | 'bank_transfer'
  | 'check'
  | 'ach'
  | 'wire'
  | 'paypal'
  | 'other';

export interface InvoiceLineItem {
  id?: number;
  line_order: number;
  line_type: LineItemType;
  product_id?: string;
  product_code?: string;
  description: string;
  quantity: number;
  unit_price_cents: number;
  amount_cents: number;
  discount_type?: 'percent' | 'fixed';
  discount_value: number;
  discount_amount_cents?: number;
  taxable: boolean;
  period_start?: string;
  period_end?: string;
  metadata?: Record<string, any>;
}

export interface Invoice {
  id: number;
  cbo_id: number;
  invoice_number: string;
  customer_contact_id?: number;
  customer_snapshot: {
    name?: string;
    email?: string;
    company?: string;
    phone?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    tax_id?: string;
  };
  opportunity_id?: string;
  status: InvoiceStatus;
  invoice_date?: string;
  due_date?: string;
  payment_terms?: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  amount_due_cents: number;
  tax_rate: number;
  discount_percent: number;
  currency: string;
  memo?: string;
  notes?: string;
  payment_instructions?: string;
  pdf_url?: string;
  portal_url?: string;
  finalized_at?: string;
  sent_at?: string;
  first_viewed_at?: string;
  paid_at?: string;
  created_at?: string;
  updated_at?: string;
  line_items?: InvoiceLineItem[];
  email_tracking?: EmailTracking[];
  payments?: InvoicePayment[];
  reminders?: InvoiceReminder[];
  portal?: PortalInfo;
}

export interface EmailTracking {
  id: number;
  recipient_email: string;
  subject?: string;
  status: string;
  provider?: string;
  sent_at?: string;
  delivered_at?: string;
  opened_at?: string;
  clicked_at?: string;
  bounced_at?: string;
  open_count: number;
  click_count: number;
  error_message?: string;
}

export interface InvoicePayment {
  id: number;
  amount_cents: number;
  payment_method: string;
  reference_number?: string;
  payment_date: string;
  source: string;
  notes?: string;
  created_at: string;
}

export interface InvoiceReminder {
  id: number;
  reminder_type: string;
  scheduled_for: string;
  status: string;
  sent_at?: string;
}

export interface PortalInfo {
  token?: string;
  url?: string;
  expires_at?: string;
  access_count: number;
  last_accessed_at?: string;
}

export interface InvoiceStats {
  draft_count: number;
  outstanding_count: number;
  overdue_count: number;
  paid_this_month: number;
  total_outstanding_cents: number;
  paid_this_month_cents: number;
}

export interface InvoiceOrgBreakdown {
  cbo_id: number;
  org_name: string;
  invoice_count: number;
  total_cents: number;
  outstanding_cents: number;
  paid_cents: number;
  draft_count: number;
}

export interface InvoiceListResponse {
  invoices: Invoice[];
  total: number;
  page: number;
  page_size: number;
  stats: InvoiceStats;
  is_reference_view?: boolean;
  by_org?: InvoiceOrgBreakdown[];
}

export interface InvoiceFilters {
  status?: InvoiceStatus;
  search?: string;
  customer_contact_id?: number;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface CreateInvoiceRequest {
  customer_contact_id?: number;
  customer_snapshot?: Record<string, any>;
  opportunity_id?: string;
  invoice_date?: string;
  due_date?: string;
  payment_terms?: string;
  tax_rate?: number;
  discount_percent?: number;
  currency?: string;
  memo?: string;
  notes?: string;
  payment_instructions?: string;
  line_items?: Array<{
    line_type?: LineItemType;
    product_id?: string;
    product_code?: string;
    description: string;
    quantity?: number;
    unit_price_cents?: number;
    amount_cents?: number;
    discount_type?: 'percent' | 'fixed';
    discount_value?: number;
    taxable?: boolean;
    period_start?: string;
    period_end?: string;
    metadata?: Record<string, any>;
  }>;
}

export interface UpdateInvoiceRequest {
  customer_contact_id?: number;
  customer_snapshot?: Record<string, any>;
  invoice_date?: string;
  due_date?: string;
  payment_terms?: string;
  tax_rate?: number;
  discount_percent?: number;
  memo?: string;
  notes?: string;
  payment_instructions?: string;
}

export interface SendInvoiceEmailRequest {
  recipient_email: string;
  subject?: string;
  message?: string;
  cc_emails?: string[];
  include_portal_link?: boolean;
  schedule_reminders?: boolean;
}

export interface RecordPaymentRequest {
  amount_cents: number;
  payment_method?: PaymentMethod;
  reference_number?: string;
  payment_date?: string;
  notes?: string;
}

export interface LineItemRequest {
  line_type?: LineItemType;
  product_id?: string;
  product_code?: string;
  description: string;
  quantity?: number;
  unit_price_cents?: number;
  amount_cents?: number;
  discount_type?: 'percent' | 'fixed';
  discount_value?: number;
  taxable?: boolean;
  period_start?: string;
  period_end?: string;
  metadata?: Record<string, any>;
}

export interface ServerInvoiceTemplate {
  id: number;
  cbo_id: number;
  name: string;
  description?: string;
  is_default: boolean;
  company_name?: string;
  logo_url?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  company_website?: string;
  primary_color: string;
  secondary_color: string;
  accent_color?: string;
  font_family?: string;
  bank_name?: string;
  bank_account_name?: string;
  bank_account_number?: string;
  bank_routing_number?: string;
  bank_swift_code?: string;
  bank_iban?: string;
  paypal_email?: string;
  stripe_enabled: boolean;
  accepted_payment_methods: string[];
  payment_method_instructions: Record<string, string>;
  payment_instructions?: string;
  terms_and_conditions?: string;
  footer_text?: string;
  default_notes?: string;
  default_due_days: number;
  tax_id?: string;
  tax_id_label?: string;
  invoice_prefix?: string;
  invoice_number_format?: string;
  show_logo: boolean;
  show_payment_instructions: boolean;
  show_bank_details: boolean;
  show_tax_id: boolean;
  show_portal_link: boolean;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  sent: 'Sent',
  viewed: 'Viewed',
  overdue: 'Overdue',
  paid: 'Paid',
  void: 'Void',
  uncollectible: 'Uncollectible',
};

export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: 'gray',
  open: 'blue',
  sent: 'yellow',
  viewed: 'purple',
  overdue: 'red',
  paid: 'green',
  void: 'slate',
  uncollectible: 'orange',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  credit_card: 'Credit Card',
  bank_transfer: 'Bank Transfer',
  check: 'Check',
  ach: 'ACH',
  wire: 'Wire Transfer',
  paypal: 'PayPal',
  other: 'Other',
};

// ============================================================================
// HELPERS
// ============================================================================

export function formatCents(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

export function centsToDecimal(cents: number): number {
  return cents / 100;
}

export function decimalToCents(amount: number): number {
  return Math.round(amount * 100);
}

// ============================================================================
// API CLIENT
// ============================================================================

class InvoiceAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      return config;
    });
  }

  // ========================================================================
  // INVOICE CRUD
  // ========================================================================

  async listInvoices(cboId: number, filters?: InvoiceFilters): Promise<InvoiceListResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const response = await this.client.get<InvoiceListResponse>(
      `/api/organizations/${cboId}/invoices?${params.toString()}`
    );
    return response.data;
  }

  async getInvoice(cboId: number, invoiceId: number): Promise<Invoice> {
    const response = await this.client.get<Invoice>(
      `/api/organizations/${cboId}/invoices/${invoiceId}`
    );
    return response.data;
  }

  async createInvoice(cboId: number, data: CreateInvoiceRequest): Promise<Invoice> {
    const response = await this.client.post<Invoice>(
      `/api/organizations/${cboId}/invoices`,
      data
    );
    return response.data;
  }

  async updateInvoice(cboId: number, invoiceId: number, data: UpdateInvoiceRequest): Promise<Invoice> {
    const response = await this.client.put<Invoice>(
      `/api/organizations/${cboId}/invoices/${invoiceId}`,
      data
    );
    return response.data;
  }

  async deleteInvoice(cboId: number, invoiceId: number): Promise<void> {
    await this.client.delete(`/api/organizations/${cboId}/invoices/${invoiceId}`);
  }

  // ========================================================================
  // LINE ITEMS
  // ========================================================================

  async addLineItem(cboId: number, invoiceId: number, item: LineItemRequest): Promise<Invoice> {
    const response = await this.client.post<Invoice>(
      `/api/organizations/${cboId}/invoices/${invoiceId}/line-items`,
      item
    );
    return response.data;
  }

  async updateLineItem(
    cboId: number,
    invoiceId: number,
    lineId: number,
    item: LineItemRequest
  ): Promise<Invoice> {
    const response = await this.client.put<Invoice>(
      `/api/organizations/${cboId}/invoices/${invoiceId}/line-items/${lineId}`,
      item
    );
    return response.data;
  }

  async deleteLineItem(cboId: number, invoiceId: number, lineId: number): Promise<void> {
    await this.client.delete(
      `/api/organizations/${cboId}/invoices/${invoiceId}/line-items/${lineId}`
    );
  }

  // ========================================================================
  // INVOICE ACTIONS
  // ========================================================================

  async finalizeInvoice(cboId: number, invoiceId: number): Promise<Invoice> {
    const response = await this.client.post<Invoice>(
      `/api/organizations/${cboId}/invoices/${invoiceId}/finalize`
    );
    return response.data;
  }

  async voidInvoice(cboId: number, invoiceId: number, reason?: string): Promise<{ message: string }> {
    const response = await this.client.post(
      `/api/organizations/${cboId}/invoices/${invoiceId}/void`,
      null,
      { params: { reason } }
    );
    return response.data;
  }

  async recordPayment(
    cboId: number,
    invoiceId: number,
    data: RecordPaymentRequest
  ): Promise<{ message: string; status: string; amount_due_cents: number }> {
    const response = await this.client.post(
      `/api/organizations/${cboId}/invoices/${invoiceId}/mark-paid`,
      data
    );
    return response.data;
  }

  // ========================================================================
  // EMAIL
  // ========================================================================

  async sendInvoiceEmail(
    cboId: number,
    invoiceId: number,
    data: SendInvoiceEmailRequest
  ): Promise<{ status: string; tracking_id: number; portal_url?: string }> {
    const response = await this.client.post(
      `/api/organizations/${cboId}/invoices/${invoiceId}/send-email`,
      data
    );
    return response.data;
  }

  async getEmailTracking(cboId: number, invoiceId: number): Promise<{ tracking: EmailTracking[] }> {
    const response = await this.client.get(
      `/api/organizations/${cboId}/invoices/${invoiceId}/tracking`
    );
    return response.data;
  }

  // ========================================================================
  // PORTAL
  // ========================================================================

  async generatePortalLink(
    cboId: number,
    invoiceId: number
  ): Promise<{ portal_url: string }> {
    const response = await this.client.post(
      `/api/organizations/${cboId}/invoices/${invoiceId}/portal-link`
    );
    return response.data;
  }

  // ========================================================================
  // PDF
  // ========================================================================

  async downloadPdf(cboId: number, invoiceId: number): Promise<Blob> {
    const response = await this.client.get(
      `/api/organizations/${cboId}/invoices/${invoiceId}/pdf`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async openPdfInNewTab(cboId: number, invoiceId: number): Promise<void> {
    const blob = await this.downloadPdf(cboId, invoiceId);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    // Clean up after a delay
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }

  // ========================================================================
  // CREATE FROM OPPORTUNITY
  // ========================================================================

  async createFromOpportunity(
    cboId: number,
    opportunityId: string,
    lineItems: LineItemRequest[],
    options?: {
      invoice_date?: string;
      due_date?: string;
      payment_terms?: string;
      tax_rate?: number;
      memo?: string;
      notes?: string;
    }
  ): Promise<Invoice> {
    // Create invoice with opportunity reference, then add line items
    const invoice = await this.createInvoice(cboId, {
      opportunity_id: opportunityId,
      line_items: lineItems,
      ...options,
    });
    return invoice;
  }

  // ========================================================================
  // PORTAL VIEW (Public - no auth needed)
  // ========================================================================

  async getPortalInvoice(token: string): Promise<{
    invoice: any;
    line_items: any[];
    branding: any;
    payments: any[];
  }> {
    const response = await this.client.get(`/api/invoice-portal/${token}`);
    return response.data;
  }

  async downloadPortalPdf(token: string): Promise<Blob> {
    const response = await this.client.get(`/api/invoice-portal/${token}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async recordPortalPayment(
    token: string,
    data: RecordPaymentRequest
  ): Promise<{ message: string; status: string; amount_due_cents: number }> {
    const response = await this.client.post(
      `/api/invoice-portal/${token}/record-payment`,
      data
    );
    return response.data;
  }

  // ========================================================================
  // INVOICE TEMPLATES (server-side persistence)
  // ========================================================================

  async listTemplates(cboId: number): Promise<ServerInvoiceTemplate[]> {
    const response = await this.client.get<{ templates: ServerInvoiceTemplate[] }>(
      `/api/organizations/${cboId}/invoice-templates`
    );
    return response.data.templates;
  }

  async createTemplate(cboId: number, data: Omit<ServerInvoiceTemplate, 'id' | 'cbo_id' | 'created_at' | 'updated_at'>): Promise<ServerInvoiceTemplate> {
    const response = await this.client.post<ServerInvoiceTemplate>(
      `/api/organizations/${cboId}/invoice-templates`,
      data
    );
    return response.data;
  }

  async updateTemplate(cboId: number, templateId: number, data: Partial<ServerInvoiceTemplate>): Promise<ServerInvoiceTemplate> {
    const response = await this.client.put<ServerInvoiceTemplate>(
      `/api/organizations/${cboId}/invoice-templates/${templateId}`,
      data
    );
    return response.data;
  }

  async deleteTemplate(cboId: number, templateId: number): Promise<void> {
    await this.client.delete(`/api/organizations/${cboId}/invoice-templates/${templateId}`);
  }
}

// Export singleton instance
export const invoiceAPI = new InvoiceAPI();
